// ═══════════════════════════════════════════════════════════════════
//  ATLAS — Multi-Document Workspace Types
// ═══════════════════════════════════════════════════════════════════
//
//  An Atlas is a top-level container that organizes multiple Documents
//  into a navigable workspace with cross-document references.
//
//  See: documents/v1/ATLAS.md for full architecture.
//
//  Three layers of loading:
//    Layer 1: Manifest (always loaded, ~50-100 KB)
//    Layer 2: Document skeletons (on demand, ~1-5 KB each)
//    Layer 3: Full sheet content (on demand, ~10-50 KB each)
//
//  Backwards compatibility: Atlas is opt-in. Existing Documents are
//  unchanged. Without an atlas.json, FW behaves as today.
//
// ═══════════════════════════════════════════════════════════════════

import type { WorkflowSheet, WorkflowBlock } from './workflow';

// ───────────────────────────────────────────────────────────────────
//  Atlas root
// ───────────────────────────────────────────────────────────────────

/** Root atlas manifest — typically stored as `atlas.json`. */
export interface Atlas {
  /** Schema version. Bump when making breaking changes to this shape. */
  schemaVersion: 1;

  /** Stable identifier. Used in URLs and as foreign-key target. */
  id: string;

  /** Display name shown in workspace browser header. */
  name: string;

  /** Optional long-form description. Markdown supported. */
  description?: string;

  /** User-managed semantic version (e.g., "1.0.0"). For change tracking. */
  version: string;

  /** Categories used to group documents in workspace browser. */
  categories: AtlasCategory[];

  /** Documents that belong to this atlas. */
  documents: AtlasDocument[];

  /**
   * Typed references between blocks in different documents.
   * Computed reverse-index: see `buildReverseIndex` in atlasLoader.
   */
  crossDocumentRefs: CrossDocumentRef[];

  /** Optional rendering hints, prefetch policy, etc. */
  settings?: AtlasSettings;

  /**
   * Optional absolute base URL where the document/sheet files live.
   * When set, the file resolver tries this base as a fallback if the
   * manifest-relative path 404s. Avoids hardcoded GitHub-raw URLs in
   * the resolver. Trailing slash is appended if missing.
   */
  fileBaseUrl?: string;

  /** Created/updated timestamps for the manifest itself. */
  createdAt?: string;
  updatedAt?: string;
}

// ───────────────────────────────────────────────────────────────────
//  Categories
// ───────────────────────────────────────────────────────────────────

/** A category groups documents in the workspace browser. */
export interface AtlasCategory {
  /** Stable identifier referenced by AtlasDocument.category. */
  id: string;

  /** Display label. */
  label: string;

  /** Optional CSS color (hex/hsl). Used as accent in browser cards. */
  color?: string;

  /** Optional lucide icon name. */
  icon?: string;

  /** Sort order in workspace browser. Lower numbers appear first. */
  sortOrder?: number;

  /** Optional human description. */
  description?: string;
}

// ───────────────────────────────────────────────────────────────────
//  Documents
// ───────────────────────────────────────────────────────────────────

/**
 * A document entry in the atlas. Points to a file (or DB record) that
 * contains the actual MultiSheetWorkflowState.
 *
 * The document itself is NOT embedded — only metadata. Full content
 * is loaded lazily when the user opens the document.
 */
export interface AtlasDocument {
  /** Stable identifier. Used as foreign-key in CrossDocumentRef. */
  id: string;

  /**
   * File path relative to the atlas.json location.
   * Example: "kalender.map.sheet1.json"
   *
   * Multi-sheet documents store all sheets in one file (the
   * MultiSheetWorkflowState format). Single-sheet documents that span
   * multiple files (legacy gotland-hub style) get one AtlasDocument
   * per file, OR one AtlasDocument with sheets[] listing all files.
   */
  file: string;

  /** Display title in workspace browser. */
  title: string;

  /** Foreign key to AtlasCategory.id. */
  category: string;

  /** Optional lucide icon name. */
  icon?: string;

  /** Searchable tags. Used for filter chips and AI context. */
  tags?: string[];

  /**
   * Summary of sheets within this document. Loaded eagerly with the
   * manifest so users see "Document A has 3 sheets" without opening it.
   *
   * Populated by `generate-atlas-manifest.mjs` (or built at runtime
   * from document content the first time it's loaded).
   */
  sheets: SheetSummary[];

  /**
   * Manually-curated list of related document IDs.
   * Distinct from CrossDocumentRef[] which describes specific block-level links.
   */
  related?: string[];

  /** Optional thumbnail URL (auto-generated by FW or user-provided). */
  thumbnailUrl?: string;

  /** Optional sort order within category. */
  sortOrder?: number;

  /**
   * Original repository file path this document was generated from
   * (atlas-from-probe output). When present, callers can fan source
   * files out to per-document attachments. Absent for hand-authored or
   * synthetic documents (pages-map, drill-down).
   */
  sourcePath?: string;
}

/** Lightweight summary of a sheet — no block content. */
export interface SheetSummary {
  /** Sheet ID. Stable across edits. */
  id: string;

  /** Display title. */
  title: string;

  /** Number of blocks (for UI hints / size estimation). */
  blockCount: number;

  /** Number of connectors. */
  connectorCount?: number;

  /** Number of groups. */
  groupCount?: number;

  /** Optional file path if sheet lives in its own file (legacy). */
  file?: string;

  /**
   * Optional inline sheet payload — short-circuits the fetch path when
   * present. Used by self-contained manifests (e.g. atlas-from-probe
   * output) so the user doesn't need to ship companion sheet files.
   * Accepts any shape the sheet-converter understands (gotland-hub
   * compact, FW single/multi-sheet). Kept as `unknown` so the type
   * file stays decoupled from the converter.
   */
  inline?: unknown;
}

// ───────────────────────────────────────────────────────────────────
//  Cross-Document References
// ───────────────────────────────────────────────────────────────────

/**
 * A typed link between blocks in different documents.
 *
 * Refs are stored in the manifest so they can be indexed without
 * loading any document content. They survive renames because they
 * use stable block IDs (UUIDs).
 */
export interface CrossDocumentRef {
  /** Stable identifier for this ref. */
  id: string;

  /** Source document. */
  fromDocumentId: string;

  /** Optional source sheet (default: implied by fromBlockId). */
  fromSheetId?: string;

  /** Optional source block. If omitted, ref is at document level. */
  fromBlockId?: string;

  /** Target document. */
  toDocumentId: string;

  /** Optional target sheet. */
  toSheetId?: string;

  /** Optional target block. */
  toBlockId?: string;

  /**
   * Semantic type of relation. Free-form string; common values:
   *   'uses', 'implements', 'audited-by', 'see-also', 'depends-on',
   *   'extends', 'replaces', 'mentions'
   */
  type: CrossDocumentRefType | string;

  /** Optional human-readable label shown in UI. */
  label?: string;

  /** Optional bidirectional flag. Default: false (one-way). */
  bidirectional?: boolean;
}

/** Suggested standard ref types — strings allowed for extensibility. */
export type CrossDocumentRefType =
  | 'uses'           // A uses B (B is a dependency)
  | 'implements'     // A implements interface/contract B
  | 'audited-by'     // A is audited by B (compliance)
  | 'see-also'       // Related, no semantic relationship
  | 'depends-on'     // A requires B to function
  | 'extends'        // A extends/refines B
  | 'replaces'       // A replaces B (deprecation marker)
  | 'mentions';      // A mentions B in text/markdown

// ───────────────────────────────────────────────────────────────────
//  Settings
// ───────────────────────────────────────────────────────────────────

export interface AtlasSettings {
  /** Default category to filter on when opening workspace browser. */
  defaultCategory?: string;

  /** Layout for workspace browser ('grid' | 'list' | 'graph'). */
  defaultLayout?: 'grid' | 'list' | 'graph';

  /**
   * Prefetch policy:
   *   'none'     — only load on explicit open
   *   'related'  — prefetch documents listed in `related[]`
   *   'all'      — prefetch all skeletons (Layer 2) on atlas load
   *   'visible'  — prefetch only when user views workspace browser
   */
  prefetchPolicy?: 'none' | 'related' | 'all' | 'visible';

  /** Whether to enable mega-canvas view (Phase 4). Default false. */
  enableMegaCanvas?: boolean;

  /** Whether to show cross-doc reference badges on blocks. Default true. */
  showRefBadges?: boolean;

  /**
   * When true, the validator skips emitting the `document-not-referenced`
   * / `document-does-not-reference` / `document-isolated` warnings. Use
   * this for atlases where the cross-document ref graph is sparse by
   * design (e.g. a hub-and-spoke layout where most docs are intentional
   * leaves). Errors and ref-target-mismatch warnings still fire.
   * Default false.
   */
  suppressIsolatedWarnings?: boolean;
}

// ───────────────────────────────────────────────────────────────────
//  Sheet-Link Block Extension
// ───────────────────────────────────────────────────────────────────

/**
 * A sheet-link block points to another sheet (potentially in another
 * document). When clicked, navigates the user to that sheet.
 *
 * Stored in `BlockConfig.linkTarget` (added in this RFC).
 *
 * Note: This is a separate concept from `CrossDocumentRef`. Sheet-link
 * blocks are USER-VISIBLE navigation points rendered in the canvas.
 * CrossDocumentRefs are METADATA used for indexing and AI context.
 *
 * A sheet-link block typically also generates an implicit
 * CrossDocumentRef (auto-derived) so search/AI can find it.
 */
export interface SheetLinkTarget {
  /** Target document. Required. */
  documentId: string;

  /** Optional target sheet (default: first sheet of document). */
  sheetId?: string;

  /**
   * Optional target block. If set, FW scrolls and highlights this block
   * after navigation. If absent, FW fits the canvas to viewport.
   */
  blockId?: string;

  /**
   * Optional human-friendly description shown in tooltip.
   * Falls back to `Block.config.description` if absent.
   */
  description?: string;
}

// ───────────────────────────────────────────────────────────────────
//  Computed structures (built by atlasLoader)
// ───────────────────────────────────────────────────────────────────

/**
 * Reverse-index built from CrossDocumentRef[].
 * Maps: documentId → list of refs that POINT TO this document.
 *
 * Used to answer "Which other documents reference Document X?"
 * without scanning all refs at query time.
 */
export type ReverseRefIndex = Map<string, CrossDocumentRef[]>;

/**
 * Block-level reverse-index. Maps: blockId → refs targeting this block.
 * Built lazily when a document is opened and its blocks are known.
 */
export type BlockRefIndex = Map<string, CrossDocumentRef[]>;

/** Search index entry — one per block across all documents in atlas. */
export interface AtlasSearchEntry {
  documentId: string;
  documentTitle: string;
  sheetId: string;
  sheetTitle: string;
  blockId: string;
  blockName: string;
  blockType: string;
  blockDescription?: string;
  /** Lower-cased concatenation for fast `.includes()` matching. */
  searchText: string;
}

// ───────────────────────────────────────────────────────────────────
//  Validation results
// ───────────────────────────────────────────────────────────────────

/** Result of validating an atlas manifest at load time. */
export interface AtlasValidationResult {
  valid: boolean;
  errors: AtlasValidationError[];
  warnings: AtlasValidationWarning[];
}

export interface AtlasValidationError {
  code:
    | 'invalid-schema-version'
    | 'duplicate-document-id'
    | 'duplicate-category-id'
    | 'unknown-category'
    | 'unknown-document-in-ref'
    | 'self-ref'
    | 'missing-required-field';
  message: string;
  path?: string; // JSON-pointer-like path
}

export interface AtlasValidationWarning {
  code:
    | 'orphan-document'           // (deprecated alias) document not in any ref
    | 'document-not-referenced'   // no other document references THIS one
    | 'document-does-not-reference' // THIS document has no outbound refs
    | 'document-isolated'         // neither inbound nor outbound refs
    | 'broken-ref-target'         // ref points to deleted block
    | 'duplicate-ref'             // two refs with same from/to
    | 'unknown-ref-type'          // type not in standard set
    | 'unused-category'           // category has zero documents
    | 'sheet-file-unreachable';   // HEAD pre-flight: sheet URL returns 4xx/5xx
  message: string;
  path?: string;
}

// ───────────────────────────────────────────────────────────────────
//  Type guards
// ───────────────────────────────────────────────────────────────────

/** Type guard: is this object an Atlas manifest? */
export function isAtlas(obj: unknown): obj is Atlas {
  if (obj == null || typeof obj !== 'object') return false;
  const a = obj as Record<string, unknown>;
  return (
    a.schemaVersion === 1 &&
    typeof a.id === 'string' &&
    typeof a.name === 'string' &&
    Array.isArray(a.categories) &&
    Array.isArray(a.documents) &&
    Array.isArray(a.crossDocumentRefs)
  );
}

/** Type guard: does this BlockConfig have a sheet-link target? */
export function hasLinkTarget(
  config: { linkTarget?: SheetLinkTarget } & Record<string, unknown>,
): config is { linkTarget: SheetLinkTarget } & Record<string, unknown> {
  return (
    config.linkTarget != null &&
    typeof config.linkTarget === 'object' &&
    typeof (config.linkTarget as SheetLinkTarget).documentId === 'string'
  );
}

// Re-export for callers that want to construct refs from existing types.
export type { WorkflowSheet, WorkflowBlock };
